'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { PowerEfficiencyFlagFragmentFragmentDoc } from '@packages/gql/generated/shopping/PowerEfficiencyFlagFragmentFragmentDoc';
import { EfficiencyFlagOverlayDialog } from '@packages/modules/src/EfficiencyFlagOverlayDialog/EfficiencyFlagOverlayDialog';
import {
  EnergyEfficiencyFlag,
  type EnergyEfficiencyFlagProps,
} from '@packages/shared/src/ui/EnergyEfficiencyFlag/EnergyEfficiencyFlag';
import { getValidColorAndEfficiencyClass } from '@packages/shared/src/ui/EnergyEfficiencyFlag/colorsForEnergyEfficiency';
import { useIntl } from 'react-intl';
import { useLayerTracking } from '../../Tracking/hooks/useLayerTracking/useLayerTracking';
import { powerEfficiencyFlagMessages } from './messages';

/* GraphQL */ `
  fragment PowerEfficiencyFlagFragment on PowerEfficiencyFlag {
    color
    level
    link
    text
    hasOldLabel
  }
`;

export type PowerEfficiencyFlagProps = {
  data: FragmentType<typeof PowerEfficiencyFlagFragmentFragmentDoc>;
  showLabel?: boolean;
} & Pick<EnergyEfficiencyFlagProps, 'size'>;

export const PowerEfficiencyFlag = ({
  data,
  showLabel = false,
  size = 'large',
}: PowerEfficiencyFlagProps) => {
  const { color, hasOldLabel, level, link, text } = getFragmentData(
    PowerEfficiencyFlagFragmentFragmentDoc,
    data,
  );

  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();

  const handleOnDialogOpen = () => {
    trackViewLayer('EEK Label(powerEfficiencyFlag)');
  };

  const colorAndEfficiencyClass = getValidColorAndEfficiencyClass(color);

  if (colorAndEfficiencyClass === undefined) {
    return null;
  }

  const flag = hasOldLabel ? (
    <EnergyEfficiencyFlag
      variant="legacy"
      text={level}
      color={colorAndEfficiencyClass.color}
      size={size}
      aria-label={formatMessage(powerEfficiencyFlagMessages.ariaLabel, {
        energyClass: level,
      })}
    />
  ) : (
    <EnergyEfficiencyFlag
      variant="new"
      efficiencyClass={colorAndEfficiencyClass.efficiencyClass}
      size={size}
      aria-label={formatMessage(powerEfficiencyFlagMessages.ariaLabel, {
        energyClass: colorAndEfficiencyClass.efficiencyClass,
      })}
    />
  );

  return (
    <EfficiencyFlagOverlayDialog
      overlay={{
        title: text,
        imgUrl: link,
      }}
      onDialogOpen={handleOnDialogOpen}
    >
      {showLabel ? (
        <span className="flex flex-row flex-nowrap items-center gap-1">
          <span>{flag}</span>
          <span>{text}</span>
        </span>
      ) : (
        flag
      )}
    </EfficiencyFlagOverlayDialog>
  );
};
